import { ToolIcon } from "@/components/molecules/tool-icon";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  ArrowLeftIcon,
  CaretDownIcon,
  CheckIcon,
  PhoneIncomingIcon,
} from "@phosphor-icons/react";
import { useState } from "react";
import type { OnboardingDraft } from "@/features/onboarding/types";
import { useAvailableTools } from "@/features/tools/hooks/use-tools";
import { useTranslation } from "@/features/utils/i18n";
import { useOnboardingChat } from "./chat";
import { ScheduleSummary } from "./schedule-summary";
import { OnboardingLayout } from "./layout";

/**
 * The tools the client actually chooses between.
 *
 * `basic_tools` is deliberately absent: transferring a call to a human is not
 * a feature anyone declines, so it is on for every tenant and putting it here
 * would only add a tile that must never be switched off. The backend forces it
 * on regardless of what this screen sends.
 */
const OFFERED_TOOLS = ["booking", "qualifications"];

/**
 * Behind "Voir plus": modules that need no decision to be useful. The FAQ and
 * the Messagerie are on by default (they build themselves from the calls), the
 * after-call SMS off (texting customers is the tenant's call). Kept out of the
 * main row so the one question asked here — do you book, do you qualify — is
 * not buried under options that can wait for the Outils page.
 */
const MORE_TOOLS = ["faq", "messages", "post_call_sms"];

/**
 * The last step — when the agent answers, and what it can do.
 *
 * Two things on one screen because they're both "confirm what we guessed": the
 * hours came off the website, and the tool selection came from what kind of
 * business it looks like. Splitting them would add a screen without adding a
 * decision.
 *
 * The hours use the back-office's own `RRulesEditor`, on the same `rrules`
 * string the schedules page edits — one representation end to end, and the
 * client meets the component they'll see again later. Its holiday section is
 * hidden here: closing days are a later concern and would bury the week.
 */
export function StepHours({
  draft,
  onChange,
  onComplete,
  onBack,
  isSubmitting,
}: {
  draft: OnboardingDraft;
  onChange: (patch: OnboardingDraft) => void;
  /**
   * Apply the setup. `callMe` also has the agent ring the client straight
   * away: hearing it is what the whole setup was for, so it is the main way
   * out of this last step rather than a button on the next screen.
   */
  onComplete: (options: { callMe: boolean }) => void;
  onBack: () => void;
  isSubmitting?: boolean;
}) {
  const { t } = useTranslation("onboarding");
  const { agentName } = useOnboardingChat();
  const { data: availableTools = [] } = useAvailableTools();

  const selectedTools = draft.tools || [];

  const toggleTool = (name: string, enabled: boolean) =>
    onChange({
      tools: enabled
        ? [...new Set([...selectedTools, name])]
        : selectedTools.filter((tool) => tool !== name),
    });

  const pick = (names: string[]) =>
    names
      .map((name) => availableTools.find((entry) => entry.tool.name === name))
      .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));
  const offered = pick(OFFERED_TOOLS);
  const more = pick(MORE_TOOLS);
  const [showMore, setShowMore] = useState(false);

  return (
    <OnboardingLayout
      step="hours"
      title={t("steps.hours.intro")}
      footer={
        <div className="space-y-3">
          <Button
            type="button"
            className="w-full"
            onClick={() => onComplete({ callMe: true })}
            disabled={isSubmitting}
          >
            <PhoneIncomingIcon className="h-4 w-4" weight="fill" />
            {isSubmitting
              ? t("actions.saving")
              : t("steps.done.callMe", {
                  name: agentName?.trim() || t("chat.agentFallback"),
                })}
          </Button>
          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" size="sm" onClick={onBack}>
              <ArrowLeftIcon className="h-4 w-4" />
              {t("actions.back")}
            </Button>
            <div className="flex-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onComplete({ callMe: false })}
              disabled={isSubmitting}
            >
              {t("steps.hours.showNumber")}
            </Button>
          </div>
        </div>
      }
    >
      <ScheduleSummary
        rrules={draft.schedule_rrules || ""}
        onChange={(rrules) => onChange({ schedule_rrules: rrules })}
      />

      {offered.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-medium">{t("steps.hours.toolsTitle")}</h2>

          {/* Tiles rather than rows: two short choices read faster side by
              side. Each carries its own one-liner — a label like "Qualification"
              names the feature without saying what it does to a caller, and
              that is precisely the decision being asked for here. */}
          <div className="grid grid-cols-2 gap-3">
            {offered.map(({ tool }) => {
              const checked = selectedTools.includes(tool.name);
              return (
                <button
                  key={tool.id}
                  type="button"
                  aria-pressed={checked}
                  onClick={() => toggleTool(tool.name, !checked)}
                  className={
                    "flex min-h-40 flex-col items-start gap-2 rounded-xl border p-3 text-left transition-colors " +
                    (checked
                      ? "border-primary bg-primary/5"
                      : "hover:bg-slate-500/5")
                  }
                >
                  <ToolIcon
                    toolName={tool.name}
                    className={
                      "h-6 w-6 " +
                      (checked ? "text-primary" : "text-muted-foreground")
                    }
                  />
                  <span className="text-sm leading-tight font-medium">
                    {t(`steps.hours.tools.${tool.name}.label`)}
                  </span>
                  <span className="text-muted-foreground line-clamp-4 text-xs leading-snug">
                    {t(`steps.hours.tools.${tool.name}.description`)}
                  </span>
                  <span
                    className={
                      "mt-auto flex h-5 w-5 items-center justify-center rounded-full border transition-colors " +
                      (checked
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-slate-500/40")
                    }
                  >
                    {checked && <CheckIcon className="h-3 w-3" weight="bold" />}
                  </span>
                </button>
              );
            })}
          </div>

          {more.length > 0 &&
            (showMore ? (
              <div className="space-y-3 rounded-xl border p-3">
                {more.map(({ tool }) => (
                  <label
                    key={tool.id}
                    className="flex cursor-pointer items-start gap-3"
                  >
                    <ToolIcon
                      toolName={tool.name}
                      className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0"
                    />
                    <span className="grid flex-1 gap-0.5">
                      <span className="text-sm leading-tight font-medium">
                        {t(`steps.hours.tools.${tool.name}.label`)}
                      </span>
                      <span className="text-muted-foreground text-xs leading-snug">
                        {t(`steps.hours.tools.${tool.name}.description`)}
                      </span>
                    </span>
                    <Switch
                      checked={selectedTools.includes(tool.name)}
                      onCheckedChange={(checked) =>
                        toggleTool(tool.name, checked)
                      }
                    />
                  </label>
                ))}
                <p className="text-muted-foreground border-t pt-3 text-xs">
                  {t("steps.hours.toolsLater")}
                </p>
              </div>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="-ml-2"
                onClick={() => setShowMore(true)}
              >
                <CaretDownIcon className="h-4 w-4" />
                {t("steps.hours.toolsMore")}
              </Button>
            ))}
        </div>
      )}
    </OnboardingLayout>
  );
}
